import { useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { useCommonTranslation } from 'c-translation';
import { InviteRoutes, PostAuthRoutes, PreAuthRoutes } from 'c-routes';
import type { UsageTimelineItem } from 'c-sdk';

/**
 * Human names for what user_activity_log stores: API route names (`reporting.pca.summary`), page
 * route templates (`/campaigns/:id/reporting`) and query params (`filters.scheduled`).
 *
 * Named ones are translated (`Modules.Admin.Usage.requests.*` / `.params.*`). Anything else falls
 * back to a readable form of its own name, so a new route or filter shows sensibly before anyone
 * names it here.
 */

/** API route name → translation key under `Modules.Admin.Usage.requests`. */
const REQUEST_KEYS: Record<string, string> = {
    'portal.campaign.list': 'campaignList',
    'portal.campaign.read': 'campaignDetails',
    'portal.campaign.update': 'campaignUpdate',
    'portal.campaign.schedule.list': 'campaignSchedules',
    'portal.campaign.schedule.display.list': 'campaignScheduleScreens',
    'portal.client.read': 'brandDetails',
    'portal.client.list': 'brandList',
    'portal.buyer.read': 'buyerDetails',
    'portal.buyer.list': 'buyerList',
    'portal.data_status.list': 'dataStatus',
    'portal.user.me': 'signedInUser',
    'portal.skin.read': 'skin',
    'portal.alerts': 'systemAlerts',
    'portal.activity.store': 'activity',
    'reporting.reporting': 'campaignCounts',
    'reporting.dynamic': 'dynamicReport',
    'reporting.live': 'livePerformance',
    'portal.campaign.reporting.test': 'pcaPdf',
    'reporting.pca.report': 'pcaReport',
    'reporting.pca.summary': 'pcaSummary',
    'reporting.pca.map': 'pcaMap',
    'reporting.pca.stats': 'pcaStats',
    'reporting.pca.histogram': 'pcaHistogram',
    'reporting.pca.dsp': 'pcaDsp',
    'reporting.pca.classic-booked-lines': 'pcaBookedLines',
};

/** Tracked action → translation key under `Modules.Admin.Usage.events`. */
const EVENT_KEYS = new Set([
    'export_cad',
    'generate_pca',
    'export_chart_data',
    'start_impersonation',
    'stop_impersonation',
    // Counted by the API from successful requests (UsageReportService::ACTION_ROUTES).
    'created_campaign',
    'uploaded_creatives',
    'saved_schedule',
    'sent_pca_pdf',
    // A PCA download (PowerPoint or PDF) by when it happened against the campaign's dates.
    'downloaded_pca_in_flight',
    'downloaded_pca_post_campaign',
    'downloaded_files',
    'invited_someone',
    'created_report',
]);

/** Query keys that are paging or plumbing, not a choice the user made. */
const HIDDEN_PARAMS = new Set([
    'include',
    'page',
    'perPage',
    'per_page',
    'orderBy',
    'direction',
    'showFilters',
    'showStats',
    'environment',
    'source',
    'filterFields',
    'filters',
    'readMeta',
    'include_staff',
    // The reporting page's whole UI state, encoded into one URL param: plumbing, not a choice.
    'state',
]);

/** Date pairs that read as one range. */
const DATE_PAIRS: [string, string][] = [
    ['start_date', 'end_date'],
    ['from', 'to'],
];
const DATE_KEYS = new Set(DATE_PAIRS.flat());

/** Param key (the part after any `filters.`/`filter.`/`search.` prefix) → `Modules.Admin.Usage.params` key. */
const PARAM_KEYS: Record<string, string> = {
    scheduled: 'scheduled',
    display_type: 'displayType',
    field: 'groupBy',
    fields: 'groupBy',
    view: 'view',
    tab: 'tab',
    status: 'status',
    campaign_id: 'campaign',
    impacts_percentage: 'impactsPercentage',
    expected_plays: 'expectedPlays',
    scope: 'scope',
};

/** What a list query searched, by its entity segment (`portal.client.list` → `client`) → `Modules.Admin.Usage.searchTargets` key. */
const SEARCH_TARGETS = new Set(['client', 'buyer', 'agency', 'campaign', 'creative', 'user']);

/** Pages before sign-in (`/login`) are logged too, so they need names as well. */
const routeKeyByTemplate = new Map<string, string>();
[
    ...Object.values(PostAuthRoutes as Record<string, Record<string, string>>),
    PreAuthRoutes as Record<string, string>,
    InviteRoutes as Record<string, string>,
].forEach(routes => {
    Object.entries(routes).forEach(([key, template]) => {
        if (!routeKeyByTemplate.has(template)) routeKeyByTemplate.set(template, key);
    });
});

/** `display_owner` / `displayOwner` → `Display owner`. */
export const humanise = (raw: string): string => {
    const words = raw
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[._-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A page template's name from its `PostAuthRoutes` key: `CampaignReporting` → `Campaign reporting`. */
export const pageLabel = (template: string): string => {
    const key = routeKeyByTemplate.get(template);
    return key ? humanise(key) : template;
};

export type ParamChip = { label: string; value: string };

const formatValue = (value: unknown): string => {
    if (Array.isArray(value)) return value.map(formatValue).join(', ');
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    const text = String(value);
    // Codes such as IN / UNBOOKED, snake_case field names and camelCase scopes read better humanised.
    return /^[A-Z_]+$/.test(text) ||
        /^[a-z]+(_[a-z]+)+$/.test(text) ||
        /^[a-z]+([A-Z][a-z0-9]*)+$/.test(text)
        ? humanise(text)
        : text;
};

/** `buyer_id` / `ownerId` → `buyer` / `owner`; null when the key isn't an id. */
const idSubject = (key: string): string | null => {
    const match = /^(.+?)(_id|Id)$/.exec(key);
    return match ? match[1] : null;
};

const formatIds = (value: unknown): string =>
    (Array.isArray(value) ? value : [value])
        .filter(id => id !== null && id !== undefined && id !== '')
        .map(id => `#${String(id)}`)
        .join(', ');

const dateOnly = (value: unknown): Date | null =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
        ? parseISO(value.slice(0, 10))
        : null;

/** "1–30 Oct 2026", "28 Sep – 3 Oct 2026", "from 1 Oct 2026". */
const dateRange = (start: Date | null, end: Date | null): string => {
    if (start && end) {
        if (format(start, 'MMM yyyy') === format(end, 'MMM yyyy')) {
            return `${format(start, 'd')}–${format(end, 'd MMM yyyy')}`;
        }
        if (format(start, 'yyyy') === format(end, 'yyyy')) {
            return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
        }
        return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
    }
    return format((start ?? end) as Date, 'd MMM yyyy');
};

/**
 * A row's params as labelled chips, plumbing left out. A date range becomes one chip, and a search
 * sent under several keys with the same text (name, reference, id) becomes one. Given the query
 * that sent them, a search says what it searched ("Search buyers"): a page that looks up brands
 * and buyers in its pickers otherwise shows two bare "Search" chips.
 */
export const useParamChips = () => {
    const t = useCommonTranslation();

    return useCallback(
        (params: UsageTimelineItem['params'], requestName?: string): ParamChip[] => {
            if (!params) return [];
            const entity = requestName?.split('.')[1];
            let searchLabel = t('Modules.Admin.Usage.params.search');
            if (entity) {
                searchLabel = t('Modules.Admin.Usage.params.searchIn', {
                    what: SEARCH_TARGETS.has(entity)
                        ? t(`Modules.Admin.Usage.searchTargets.${entity}`)
                        : humanise(entity).toLowerCase(),
                });
            }
            const chips: ParamChip[] = [];
            const searches = new Set<string>();

            DATE_PAIRS.forEach(([from, to]) => {
                const start = dateOnly(params[from]);
                const end = dateOnly(params[to]);
                if (start || end) {
                    chips.push({
                        label: t('Modules.Admin.Usage.params.dates'),
                        value: dateRange(start, end),
                    });
                }
            });

            Object.entries(params).forEach(([key, value]) => {
                const bare = key.replace(/^(filters?)\./, '');
                if (HIDDEN_PARAMS.has(key) || HIDDEN_PARAMS.has(bare) || DATE_KEYS.has(key)) return;
                const formatted = formatValue(value);
                // An unset switch says nothing about what the user chose.
                if (formatted === '' || formatted === 'false') return;

                if (bare.startsWith('search')) {
                    if (!searches.has(formatted)) {
                        searches.add(formatted);
                        chips.push({ label: searchLabel, value: formatted });
                    }
                    return;
                }

                const named = PARAM_KEYS[bare];
                const subject = named ? null : idSubject(bare);
                if (subject) {
                    chips.push({ label: humanise(subject), value: formatIds(value) });
                    return;
                }
                chips.push({
                    label: named ? t(`Modules.Admin.Usage.params.${named}`) : humanise(bare),
                    value: formatted,
                });
            });

            return chips;
        },
        [t],
    );
};

/** A timeline row's name: a page's, an action's, or an API query's, in words. */
export const useActivityLabel = () => {
    const t = useCommonTranslation();

    return useCallback(
        (item: Pick<UsageTimelineItem, 'kind' | 'name'>): string => {
            if (item.kind === 'page_view') return pageLabel(item.name);
            // `campaign_signed_off` → "Campaign signed off".
            if (item.kind === 'email') return humanise(item.name);
            if (item.kind === 'event') {
                return EVENT_KEYS.has(item.name)
                    ? t(`Modules.Admin.Usage.events.${item.name}`)
                    : humanise(item.name);
            }
            const key = REQUEST_KEYS[item.name];
            if (key) return t(`Modules.Admin.Usage.requests.${key}`);
            // `reporting.pca.summary` → `Pca summary`: the first segment is the API module.
            return humanise(item.name.split('.').slice(1).join(' ') || item.name);
        },
        [t],
    );
};

/**
 * A row's device in words: "Chrome 130 on macOS (Desktop)". Rows logged before devices were parsed
 * fall back to the raw user agent.
 */
export const useDeviceLabel = () => {
    const t = useCommonTranslation();

    return useCallback(
        (
            item: Pick<
                UsageTimelineItem,
                'browser' | 'browser_version' | 'os' | 'device_type' | 'user_agent'
            >,
        ): string => {
            if (!item.browser && !item.os) return item.user_agent ?? '';
            const browser = [item.browser, item.browser_version].filter(Boolean).join(' ');
            const where = item.os
                ? t('Modules.Admin.Usage.deviceOn', { browser: browser || '?', os: item.os })
                : browser;
            return item.device_type
                ? `${where} (${t(`Modules.Admin.Usage.deviceTypes.${item.device_type}`)})`
                : where;
        },
        [t],
    );
};
